import React, { useState } from 'react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  ThemeProvider
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { customTimePickerTheme } from '@styles/components/mui-time-picker-styles.ts';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import { ScheduleDatePickerProps } from '@modules/digital-signage/model/playlist-schedule.model.ts';
import {
  getScheduleDateErrorKey,
  scheduleErrorMessages
} from '@modules/digital-signage/utils/schedule.utils.ts';
import { isEmptyOrValidDate } from '@modules/digital-signage/utils/schedule-date.utils.ts';

const ScheduleDatePicker: React.FC<ScheduleDatePickerProps> = ({
  name,
  label,
  control,
  errors,
  dateError,
  minDate,
  maxDate
}) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const hasError = !!errors[name] || !!dateError;
  const dateErrorKey = getScheduleDateErrorKey(dateError, name);

  return (
    <FormControl className="w-full" error={hasError}>
      <FormLabel className="flex">
        <span className="reuqired-symbole">*</span>
        {t(label)}
      </FormLabel>
      <ThemeProvider theme={customTimePickerTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Controller
            name={name}
            control={control}
            rules={{
              required: true,
              validate: { invalidDate: isEmptyOrValidDate }
            }}
            render={({ field }) => (
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                // Opening the calendar moves focus into it, so the field is
                // touched only once the picker closes or the input is left.
                open={isOpen}
                onOpen={() => setIsOpen(true)}
                onClose={() => {
                  setIsOpen(false);
                  field.onBlur();
                }}
                minDate={minDate}
                maxDate={maxDate}
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    onBlur: () => !isOpen && field.onBlur(),
                    error: hasError,
                    placeholder: t('placeholder_digital_signage_schedule_date')
                  }
                }}
              />
            )}
          />
        </LocalizationProvider>
      </ThemeProvider>
      {/* Always rendered, so the error doesn't shift the layout */}
      <FormHelperText className="min-h-5">
        {errors[name]
          ? getErrorMessage({
              errors,
              fieldName: name,
              errorMessages: scheduleErrorMessages
            })
          : dateErrorKey && t(dateErrorKey)}
      </FormHelperText>
    </FormControl>
  );
};

export default ScheduleDatePicker;
